/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IHeiStoreHoursViewProps } from './hei-store-hours';
import { OrgUnitLocation, StoreHours } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import {
    buildStoreHours,
    IHoursDisplayInfo,
    secondsToTime
} from '../store-selector/components/store-selector-location-line-item/date-time-helpers';
import { IStoreSelectorLocationLineItemResources } from '../store-selector/components/store-selector-location-line-item';

const HeiStoreHoursView = (props: IHeiStoreHoursViewProps) => {
    const {
        storeInfo: { storeHours, location, resources },
        config: { className }
    } = props;

    return <div className={classnames('hei-store-hours', className)}>{renderStoreHours(storeHours, location, resources)}</div>;
};

const renderStoreHours = (
    storeHours: StoreHours | undefined,
    location: OrgUnitLocation,
    resources: IStoreSelectorLocationLineItemResources
): JSX.Element | null => {
    // First, use the store hours. This will allow listing day by day houts
    if (storeHours && storeHours.RegularStoreHours) {
        const storeHoursList: IHoursDisplayInfo[] = buildStoreHours(storeHours.RegularStoreHours, resources);

        return (
            <div className='ms-store-select__store-hours-details-group'>
                {storeHoursList.map((displayTime, i) => (
                    <div key={i} className='ms-store-select__store-hours-details'>
                        {displayTime.endDayShort ? (
                            <div className='ms-store-select__store-hours-day'>
                                <abbr title={displayTime.startDayFull}>{displayTime.startDayShort}</abbr>–
                                <abbr title={displayTime.endDayFull}>{displayTime.endDayShort}</abbr>
                            </div>
                        ) : (
                            <div className='ms-store-select__store-hours-day'>
                                <abbr title={displayTime.startDayFull}>{displayTime.startDayShort}</abbr>
                            </div>
                        )}
                        <div className='ms-store-select__store-hours-time'>{displayTime.timeRange}</div>
                    </div>
                ))}
            </div>
        );
    } else {
        // If that fails, fall back to using the OpenFrom and OpenTo properties on the location
        const storeOpenFrom: string | undefined = secondsToTime(location.OpenFrom);
        const storeOpenTo: string | undefined = secondsToTime(location.OpenTo);

        return (
            <div className='ms-store-select__store-hours-details'>
                {storeOpenFrom}-{storeOpenTo}
            </div>
        );
    }
};

export default HeiStoreHoursView;
